import { useState, type ReactNode } from 'react';
import type { FeedbackReason, FrameRecord, MarkedRange, SessionFeedback } from '@erectus/shared';
import { Button, Card } from '@/ui/primitives.js';
import { submitFeedback } from '@/lib/repo.js';
import { count, track } from '@/lib/analytics.js';
import { Timeline, formatMinute } from './Timeline.js';

/**
 * Feedback widget (docs/DATA_AND_ML.md §4).
 *
 * Two levels: a one-tap 👍/👎, and optional range marking with a reason. The
 * reasons are the false-positive taxonomy the Phase 8 artifact detector is
 * trained on, which is why they are fixed and not free text.
 */
const REASONS: Array<{ id: FeedbackReason; label: string }> = [
  { id: 'eating', label: 'I was eating' },
  { id: 'reaching', label: 'I reached for something' },
  { id: 'camera_moved', label: 'The camera moved' },
  { id: 'person_passed', label: 'Someone walked past' },
  { id: 'actually_bad', label: 'It was right — my posture was bad' },
  { id: 'other', label: 'Something else' },
];

export function FeedbackWidget({
  uid,
  sessionId,
  frames,
  durationSec,
}: {
  uid: string;
  sessionId: string;
  frames: readonly FrameRecord[];
  durationSec: number;
}): ReactNode {
  const [overall, setOverall] = useState<'accurate' | 'inaccurate' | null>(null);
  const [marking, setMarking] = useState(false);
  const [pending, setPending] = useState<{ start: number; end: number } | null>(null);
  const [ranges, setRanges] = useState<MarkedRange[]>([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = (second: number): void => {
    setPending((prev) => {
      if (!prev || prev.end !== prev.start) return { start: second, end: second };
      return { start: Math.min(prev.start, second), end: Math.max(prev.start, second) };
    });
  };

  const addRange = (reason: FeedbackReason): void => {
    if (!pending) return;
    const range: MarkedRange =
      pending.start === pending.end
        ? { start: pending.start, end: Math.min(durationSec, pending.start + 30), reason }
        : { ...pending, reason };
    setRanges((prev) => [...prev, range].slice(0, 20));
    setPending(null);
  };

  const send = async (verdict: 'accurate' | 'inaccurate'): Promise<void> => {
    setOverall(verdict);
    setError(null);
    const feedback: SessionFeedback = {
      uid,
      overall: verdict,
      markedRanges: ranges,
      createdAt: Date.now(),
    };
    try {
      await submitFeedback(sessionId, feedback);
      setSaved(true);
      track('feedback_submit', { overall: verdict, ranges_count: count(ranges.length) });
    } catch {
      setError('Could not send your feedback. It will not block your report.');
    }
  };

  return (
    <Card className="space-y-3">
      <div>
        <h2 className="font-semibold">Did we read this session right?</h2>
        <p className="text-sm text-muted">
          This is what makes detection better over time — especially the wrong bits.
        </p>
      </div>

      <div className="flex gap-2">
        <Button
          variant={overall === 'accurate' ? 'primary' : 'secondary'}
          className="flex-1"
          onClick={() => void send('accurate')}
        >
          👍 Looks right
        </Button>
        <Button
          variant={overall === 'inaccurate' ? 'primary' : 'secondary'}
          className="flex-1"
          onClick={() => void send('inaccurate')}
        >
          👎 Something is off
        </Button>
      </div>

      {frames.length > 0 ? (
        <>
          <button className="text-sm font-semibold underline" onClick={() => setMarking((m) => !m)}>
            {marking ? 'Hide' : 'Mark the part we got wrong'}
          </button>
          {marking ? (
            <div className="space-y-3">
              <p className="text-xs text-muted">
                Tap the start, then the end of the stretch that is wrong.
              </p>
              <Timeline
                frames={frames}
                durationSec={durationSec}
                onPick={pick}
                selection={pending}
              />
              {pending ? (
                <div className="space-y-2">
                  <p className="text-sm">
                    {formatMinute(pending.start)}
                    {pending.end !== pending.start ? ` – ${formatMinute(pending.end)}` : ''} — what
                    happened?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {REASONS.map((reason) => (
                      <Button
                        key={reason.id}
                        variant="secondary"
                        className="text-xs"
                        onClick={() => addRange(reason.id)}
                      >
                        {reason.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : null}
              {ranges.length > 0 ? (
                <ul className="space-y-1 text-xs text-muted">
                  {ranges.map((range, i) => (
                    <li key={i}>
                      {formatMinute(range.start)}–{formatMinute(range.end)} ·{' '}
                      {REASONS.find((r) => r.id === range.reason)?.label ?? range.reason}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      {saved ? <p className="text-xs text-[color:var(--color-state-good)]">Thanks — saved.</p> : null}
      {error ? <p className="text-xs text-[color:var(--color-state-slouch)]">{error}</p> : null}
    </Card>
  );
}
